import { isEqual } from "lodash";
import { Dispatch, useEffect, useEffectEvent, useRef, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Pen } from "react-bootstrap-icons";
import {
  IValidReturn,
  IValidationFunction,
  IValidationTarget,
  classMerge,
  formatJson,
  loadWrapper,
} from "../utility";
import { IRequestStatus, IValidationTargetOptions } from "../utility/interface";
import { ControlBase, ControlBaseProps } from "./ControlBase";
import { CopyText } from "./CopyText";
import { InTextLink } from "./InTextLink";

/**
 * Props for the Control210 component.
 */
export interface Control210Props extends ControlBaseProps {
  id: string;
  ActionButton?: React.FC;
  allowCarriageReturn?: boolean;
  allowCopy?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  editButton?: boolean;
  forcePrefix?: string;
  invalidMessage?: string;
  isJsonText?: boolean;
  label: string;
  labelWidth?: number | "auto";
  maxWidthCols?: number;
  noInvalidReturn?: boolean;
  onEditChange?: (isEditing: boolean) => void;
  ReadComponent?: React.FC<{ value: string }>;
  rows?: number;
  setValue?: Dispatch<string>;
  spellCheck?: boolean;
  subText?: string;
  type?: "string" | "number" | "textarea";
  validCondition?: IValidationFunction;
  validMessage?: string;
  validationTargetOptions?: IValidationTargetOptions;
  validationRequestUrl?: string;
  validationReturn?: (ret: boolean) => void;
  value: string;
  whiteSpaceFix?: boolean;
}

/**
 * A versatile text input control that supports both single-line text input and multi-line textarea functionality with customizable options.
 * Features include validation, JSON formatting, copy functionality, an optional inline edit-mode pen button, and responsive Bootstrap layout.
 *
 * @param props - Component props.
 * @param props.id - Unique identifier for the control
 * @param props.ActionButton - Optional React component rendered as an additional column after the input column (e.g. an external link button)
 * @param props.allowCarriageReturn - Whether to allow carriage returns in textarea (defaults to false)
 * @param props.allowCopy - Whether to show a copy button for the content (defaults to false)
 * @param props.autoFocus - Whether the input should automatically receive focus
 * @param props.disabled - Whether the control is disabled
 * @param props.editButton - When true, renders a pen icon button that toggles the input between read-only and edit mode; Escape cancels any pending edit and reverts the value
 * @param props.forcePrefix - A prefix that must be present at the start of the value (defaults to "")
 * @param props.invalidMessage - Message to display when validation fails
 * @param props.isJsonText - Whether the content should be formatted as JSON (defaults to false)
 * @param props.label - Label text for the input field
 * @param props.labelWidth - Width of the label column in Bootstrap grid units or 'auto' (defaults to 2)
 * @param props.maxWidthCols - Maximum width in Bootstrap grid columns (defaults to 12)
 * @param props.noInvalidReturn - When true, the setValue return is not called when the validation fails
 * @param props.onEditChange - Callback invoked with the new editing state whenever edit mode is entered or exited
 * @param props.ReadComponent - Optional React component rendered with the current value when the control is disabled and showing edit-button read mode
 * @param props.rows - Number of rows for textarea type
 * @param props.setValue - Callback function to update the value
 * @param props.spellCheck - Whether spell checking is enabled (defaults to true)
 * @param props.subText - Additional help text displayed below the input
 * @param props.type - The type of input control to render (defaults to "string")
 * @param props.validCondition - Validation function to check the input value
 * @param props.validMessage - Message to display when validation passes
 * @param props.validationTargetOptions - Optional validation context options forwarded to the validation function
 * @param props.validationRequestUrl - URL for validation requests (if applicable)
 * @param props.validationReturn - Callback to receive validation results
 * @param props.value - Current value of the input
 * @param props.whiteSpaceFix - Whether to automatically trim whitespace on blur (defaults to false)
 * @param props.rest - Additional ControlBaseProps
 * @returns A React functional component rendering a Form.Group with label and input
 */
export const Control210 = ({
  id,
  ActionButton,
  allowCarriageReturn = false,
  allowCopy = false,
  autoFocus,
  disabled,
  editButton = false,
  forcePrefix = "",
  invalidMessage,
  isJsonText = false,
  label,
  labelWidth = 2,
  maxWidthCols = 12,
  noInvalidReturn,
  onEditChange,
  ReadComponent,
  rows,
  setValue,
  spellCheck = true,
  subText,
  type = "string",
  validCondition,
  validMessage,
  validationTargetOptions,
  validationRequestUrl = "",
  validationReturn,
  value,
  whiteSpaceFix = false,
  ...rest
}: Control210Props): React.ReactElement => {
  // Run validation check on current value
  const [validationStatus, setValidationStatus] = useState<IValidReturn>({
    success: false,
    errorText: "",
  });
  const [validationRequest, setValidationRequest] = useState<IRequestStatus<IValidationTarget>>({
    requesting: false,
    error: false,
  });

  // Edit-button mode state; starts as true when there is no edit button so the input is always active
  const [isEditing, setIsEditing] = useState(!editButton);
  const cancelRef = useRef(false);

  // Tracks the value the user is currently typing in edit mode so validation runs live.
  // When not in edit mode, validation uses the committed `value` prop instead.
  const [editingValue, setEditingValue] = useState(value);

  // The value to validate: use the in-progress editing value while editing, otherwise the committed prop value.
  const validationValue = editButton && isEditing ? editingValue : value;
  const lastEffectValidationTarget = useRef<IValidationTarget | undefined>(undefined);

  /**
   * Runs validation for a candidate control value and updates validation state.
   *
   * @param newValue - Candidate value and validation options to check
   * @returns The completed validation response, or null when no response is returned
   */
  const runValidation = async (newValue: IValidationTarget): Promise<IValidReturn | null> => {
    if (!validCondition) {
      const response: IValidReturn = {
        success: true,
        data: {
          valid: true,
          validationText: "",
        },
      };
      setValidationStatus(response);
      setValidationRequest({ requesting: false, error: false, requestedId: newValue });
      return response;
    } else {
      const response = await loadWrapper(
        validCondition,
        newValue,
        { ...validationRequest, error: false, errorText: undefined },
        (ret) => {
          // Report the current name as not-yet-valid as soon as a new validation check starts if a check needs to occur
          // consumers (e.g. save/set buttons) cannot act on a stale valid result while this one is pending.
          if (ret.requesting) validationReturn?.(false);
          setValidationRequest(ret);
        },
        validationRequestUrl,
      );
      /* istanbul ignore else */
      if (response) {
        setValidationStatus(response);
        validationReturn?.(response.success === false ? false : response.data.valid);
      }
      return response;
    }
  };

  const checkValidation = useEffectEvent(runValidation);

  /**
   * Updates the committed value after completing validation for the proposed value.
   *
   * @param newValue - Value produced by the input control
   */
  const setValidatedValue = async (newValue: string) => {
    if (validCondition) {
      const response = await runValidation({ value: newValue, options: validationTargetOptions });
      const isValid = response?.success === true && response.data.valid === true;
      if (!noInvalidReturn || isValid) {
        setValue?.(newValue);
      }
    } else {
      setValue?.(newValue);
    }
  };

  /** Call validation when the value or options change */
  useEffect(() => {
    const target: IValidationTarget = { value: validationValue, options: validationTargetOptions };
    if (!isEqual(target, lastEffectValidationTarget.current)) {
      lastEffectValidationTarget.current = target;
      checkValidation(target);
    }
  }, [validationRequest.requesting, validationTargetOptions, validationValue]);

  /**
   * Handles entering edit mode when the pen button is clicked.
   * Captures the current value in the edit buffer and notifies the parent.
   */
  const handlePenClick = () => {
    setEditingValue(value);
    setIsEditing(true);
    onEditChange?.(true);
  };

  /**
   * Captures key events on the input wrapper to handle Escape (cancel) and Enter (confirm) in edit mode.
   * For Escape: sets the cancel flag and blurs the focused input so the blur handler discards the edit.
   * For Enter (commit): blurs the focused input so the blur handler commits the edit.
   * Enter commits in all cases except Shift+Enter in a textarea with allowCarriageReturn, where
   * Shift+Enter instead inserts a newline — so plain Enter submits and Shift+Enter creates a new line.
   *
   * @param e - The keyboard event fired during the capture phase
   */
  const handleKeyDownCapture = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (editButton && isEditing) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        cancelRef.current = true;
        const target = e.currentTarget.querySelector("input, textarea");
        /* istanbul ignore else */
        if (target instanceof HTMLElement) {
          target.blur();
        }
      } else if (e.key === "Enter" && !(e.shiftKey && allowCarriageReturn && type === "textarea")) {
        e.preventDefault();
        const target = e.currentTarget.querySelector("input, textarea");
        /* istanbul ignore else */
        if (target instanceof HTMLElement) {
          target.blur();
        }
      }
    }
  };

  /**
   * Handles blur events on the input wrapper div.
   * When the cancel flag is set (Escape was pressed), exits edit mode without committing any change.
   * Otherwise, commits the current field value to the external setValue and exits edit mode.
   *
   * @param e - The focus event from the blur
   */
  const handleEditWrapperBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!editButton) return;
    // Ignore focus moving to a child element (e.g. clicking inside the same wrapper)
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    /* istanbul ignore else */
    if (cancelRef.current) {
      cancelRef.current = false;
      setIsEditing(false);
      onEditChange?.(false);
    } else if (isEditing) {
      const input = e.currentTarget.querySelector("input, textarea");
      const commitValue =
        input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement
          ? input.value
          : editingValue;
      void setValidatedValue(commitValue);
      setIsEditing(false);
      onEditChange?.(false);
    }
  };

  // Calculate extra column deductions (allowCopy and actionButton each take 1 col)
  const extraColDeduction = (allowCopy ? 1 : 0) + (ActionButton ? 1 : 0);

  // Disabled when the edit button is present but edit mode is not active, or when externally disabled/read-only
  const isInputDisabled = (editButton && !isEditing) || disabled === true || !setValue;

  const validationClassName =
    validationStatus.success && !validationRequest.requesting
      ? validationStatus.data.warning
        ? "is-warning"
        : validationStatus.data.valid === true
          ? "is-valid"
          : "is-invalid"
      : "";
  const combinedClassName = classMerge([rest.className, validationClassName]);
  const hasWarningState = combinedClassName.split(/\s+/).includes("is-warning");

  return (
    <Form.Group
      controlId={`${id}`}
      as={Row}
      className="my-1"
    >
      <Form.Label
        column
        md={labelWidth}
      >
        {label}
      </Form.Label>
      <Col
        md={
          labelWidth === "auto"
            ? undefined
            : Math.max(maxWidthCols - labelWidth - extraColDeduction, 1)
        }
      >
        <div
          className="d-flex align-items-center gap-1"
          onKeyDownCapture={handleKeyDownCapture}
          onBlur={handleEditWrapperBlur}
        >
          {isInputDisabled && ReadComponent ? (
            <ReadComponent value={value} />
          ) : (
            <ControlBase
              {...rest}
              key={editButton ? `${id}-${isEditing}` : id}
              value={isJsonText ? formatJson(value) : value}
              setValue={
                editButton
                  ? (ret) => {
                      setEditingValue(ret);
                    }
                  : (ret) => void setValidatedValue(ret)
              }
              whiteSpaceFix={whiteSpaceFix}
              disabled={isInputDisabled}
              type={isJsonText ? "textarea" : type}
              forcePrefix={forcePrefix}
              rows={rows}
              allowCarriageReturn={allowCarriageReturn}
              className={combinedClassName}
              spellCheck={spellCheck}
              autoFocus={editButton ? isEditing : autoFocus}
            />
          )}
          {editButton && setValue && !isEditing && (
            <Button
              variant="outline-primary"
              size="sm"
              disabled={disabled}
              onClick={handlePenClick}
              aria-label="Edit"
              title="Edit"
              style={{ flexShrink: 0 }}
            >
              <Pen />
            </Button>
          )}
        </div>
        {(subText || validCondition) && (
          <Form.Text
            className={`${
              validationStatus.success && !validationRequest.requesting
                ? hasWarningState
                  ? "warning-feedback d-block"
                  : validationStatus.data?.valid === true
                    ? "valid-feedback d-block"
                    : "invalid-feedback d-block"
                : "text-muted"
            }`}
          >
            <InTextLink
              text={[
                subText ?? "",
                validationRequest.requesting
                  ? "updating status..."
                  : validationStatus.success
                    ? validationStatus.data.validationText !== ""
                      ? validationStatus.data.validationText
                      : validationStatus.data.valid === true
                        ? (validMessage ??
                          // istanbul ignore next
                          "")
                        : (invalidMessage ??
                          // istanbul ignore next
                          "")
                    : validationStatus.errorText,
              ]
                .filter((s) => s !== "")
                .join(", ")}
            />
          </Form.Text>
        )}
      </Col>
      {allowCopy && (
        <Col xs={1}>
          <CopyText value={value} />
        </Col>
      )}
      {ActionButton && (
        <Col xs={1}>
          <ActionButton />
        </Col>
      )}
    </Form.Group>
  );
};

Control210.displayName = "Control210";
